import { z } from 'zod'

import { SubscriptionStatus } from '../enums'

// limitValue is a String on the backend (e.g. "5", "-1", "true" for BOOLEAN
// entitlements, or a byte count for FILE_UPLOAD_SIZE_LIMIT); keep it raw here and
// let the UI format it via utils/quota.ts.
const rawLimitValue = z.union([z.string(), z.number()])

// Used for status/usage responses — includes currentUsage.
export const entitlementSchema = z.object({
  featureKey: z.string(),
  name: z.string(),
  limitType: z.string(),
  limitValue: rawLimitValue,
  currentUsage: z.number().int(),
})

// Used for plan-listing responses — no currentUsage field in DTOTemplateEntitlementResponse.
export const planEntitlementSchema = z.object({
  featureKey: z.string(),
  name: z.string(),
  limitType: z.string(),
  limitValue: rawLimitValue,
})

export const subscriptionPlanSchema = z.object({
  templateId: z.string(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  durationMonths: z.number().int().positive(),
  price: z.number().nonnegative(),
  entitlements: z.array(planEntitlementSchema),
})

export const subscriptionStatusSchema = z.object({
  subscriptionId: z.string().uuid().nullable(),
  workspaceId: z.string().uuid(),
  planCode: z.string().nullable(),
  planName: z.string().nullable(),
  planPrice: z.number().nullable(),
  status: z.nativeEnum(SubscriptionStatus),
  // Backend uses LocalDate which serialises as "2024-01-01" (date-only, no time component).
  // z.string().datetime() would reject that — use z.string() instead.
  currentPeriodStart: z.string().nullable(),
  currentPeriodEnd: z.string().nullable(),
  cancelAtPeriodEnd: z.boolean(),
  entitlements: z.array(entitlementSchema),
})

export const subscriptionUsageSchema = z.object({
  workspaceId: z.string().uuid(),
  subscriptionStatus: z.string(),
  // Same LocalDate serialisation caveat as above.
  currentPeriodStart: z.string().nullable(),
  currentPeriodEnd: z.string().nullable(),
  usage: z.array(entitlementSchema),
})

export const lockedResourceSchema = z.object({
  resourceType: z.string(),
  resourceId: z.string().uuid(),
  lockedAt: z.string().datetime(),
})

export const subscribeRequestSchema = z.object({
  templateCode: z.string(),
})

export const changePlanRequestSchema = z.object({
  templateCode: z.string(),
})

export type Entitlement = z.infer<typeof entitlementSchema>
export type PlanEntitlement = z.infer<typeof planEntitlementSchema>
export type SubscriptionPlan = z.infer<typeof subscriptionPlanSchema>
export type SubscriptionStatusResponse = z.infer<typeof subscriptionStatusSchema>
export type SubscriptionUsage = z.infer<typeof subscriptionUsageSchema>
export type LockedResource = z.infer<typeof lockedResourceSchema>
export type SubscribeRequest = z.infer<typeof subscribeRequestSchema>
export type ChangePlanRequest = z.infer<typeof changePlanRequestSchema>